/**
 * useGetRegionListAddress Hook
 * Manages region, district, ward selection state and UI display
 * @see mobile: module-customer/src/modules/addresses/hooks/use-get-region-list-address.hooks.ts
 */

'use client';

import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { regionSelectedStore$ } from '../../../stores/region-selected.store';
import type { District, Ward } from '../../../types/front.graphql';
import type { RegionListValue } from '../components/region-list/region-list.types';
import type { LocationItemData, NormalizedRegion } from '../types';

export interface UseGetRegionListAddressInterface {
    shipTo: () => string;
    addressColors: () => string | undefined;
    onSetRegion: (item: NormalizedRegion) => void;
    onSetDistrict: (item: LocationItemData<District>) => void;
    onSetWard: (item: LocationItemData<Ward>) => void;
    onSetSelectedRegionStore: (regionListValue: RegionListValue) => void;
    onResetRegionStore: () => void;
    regionSelected: NormalizedRegion | undefined;
    districtSelected: (District & { name: string }) | undefined;
    wardSelected: (Ward & { name: string }) | undefined;
    onSetInitialRegionStore: () => void;
    hasAddress: () => boolean;
}

export const useGetRegionListAddress = (value: RegionListValue): UseGetRegionListAddressInterface => {
    const { t } = useTranslation('customer');
    const regionSelected = regionSelectedStore$.region.get();
    const districtSelected = regionSelectedStore$.district.get();
    const wardSelected = regionSelectedStore$.ward.get();

    const hasAddress = useCallback((): boolean => {
        return !!(regionSelected?.name && districtSelected?.name && wardSelected?.name);
    }, [regionSelected, districtSelected, wardSelected]);

    const shipTo = useCallback(() => {
        if (hasAddress()) {
            return `${wardSelected?.name}, ${districtSelected?.name}, ${regionSelected?.name}`;
        }
        return t('customer_input_region');
    }, [regionSelected, districtSelected, wardSelected, t, hasAddress]);

    const addressColors = useCallback(() => {
        // Return placeholder color when no address is selected (matching mobile theme)
        return !hasAddress() ? 'var(--color-neutral-50)' : undefined;
    }, [hasAddress]);

    const handleSetSelectedRegionStore = useCallback((regionListValue: RegionListValue) => {
        regionSelectedStore$.region.set(regionListValue.region);
        regionSelectedStore$.district.set(regionListValue.district);
        regionSelectedStore$.ward.set(regionListValue.ward);
    }, []);

    const handleResetRegionStore = useCallback(() => {
        regionSelectedStore$.region.set(undefined);
        regionSelectedStore$.district.set(undefined);
        regionSelectedStore$.ward.set(undefined);
    }, []);

    const handleSetInitialRegionStore = useCallback(() => {
        if (value) {
            handleSetSelectedRegionStore(value);
        } else {
            handleResetRegionStore();
        }
    }, [handleResetRegionStore, handleSetSelectedRegionStore, value]);

    const handleSetRegion = useCallback((item: NormalizedRegion) => {
        regionSelectedStore$.region.set(item);
        regionSelectedStore$.district.set(undefined);
        regionSelectedStore$.ward.set(undefined);
    }, []);

    const handleSetDistrict = useCallback((item: LocationItemData<District>) => {
        regionSelectedStore$.district.set(item);
        regionSelectedStore$.ward.set(undefined);
    }, []);

    const handleSetWard = useCallback((item: LocationItemData<Ward>) => {
        regionSelectedStore$.ward.set(item);
    }, []);

    useEffect(() => {
        if (value) {
            handleSetSelectedRegionStore(value);
        }
    }, [handleSetSelectedRegionStore, value]);

    return {
        shipTo,
        addressColors,
        onSetRegion: handleSetRegion,
        onSetDistrict: handleSetDistrict,
        onSetWard: handleSetWard,
        onSetSelectedRegionStore: handleSetSelectedRegionStore,
        onResetRegionStore: handleResetRegionStore,
        regionSelected,
        districtSelected,
        wardSelected,
        onSetInitialRegionStore: handleSetInitialRegionStore,
        hasAddress,
    };
};
